import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  Chip,
  Container,
  Dialog,
  DialogContent,
  Grid,
  Tooltip,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import moment from 'moment'
import { debounce } from 'lodash'
import { getRequests, setMssRequestFilters } from '@state/reducers/mssReducer'
import MssRequestsTableToolbar from './Toolbars/MssRequestsTableToolbar'
import AttachmentIcon from '@mui/icons-material/Attachment'
import { MssRequestDto, MssRequestStatus } from '@services/dtos'
import {
  MssRequestPriorityLabels,
  MssRequestStatusLabels,
} from '@utils/constants'
import { UpTransition } from '../animations'
import MssRequestForm from '../forms/MssRequestForm'
import { EditIcon } from 'lucide-react'

export default function MssRequestsTable() {
  const [editingRequest, setEditingRequest] = useState<MssRequestDto | null>(
    null,
  )

  const dispatch = useAppDispatch()
  const { requests, loading, filters } = useAppSelector(({ mss }) => ({
    requests: mss.requests,
    loading: mss.requestLoading,
    filters: mss.requestFilters,
  }))

  useEffect(() => {
    const debouncedSearch = debounce(() => {
      dispatch(getRequests())
    }, 300)

    debouncedSearch()

    return () => {
      debouncedSearch.cancel()
    }
  }, [filters, dispatch])

  const actionBtnObj = [
    {
      id: 1,
      btnName: 'Modifier',
      icon: <EditIcon color="info" />,
      action: (row: MssRequestDto) => setEditingRequest(row),
      actionKey: 'editRequest',
    },
  ]

  const columns: GridColumns = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const request = params.row
        return (
          <div className="flex space-x-2">
            {actionBtnObj.reduce((acc, el) => {
              acc.push(
                <Tooltip key={el.btnName} title={el.btnName}>
                  <div
                    className={'cursor-pointer p-1 hover:bg-gray-200 rounded'}
                    onClick={(e) => {
                      e.stopPropagation()
                      const action = el.action.bind({})
                      action(request)
                    }}
                  >
                    {el.icon}
                  </div>
                </Tooltip>,
              )
              return acc
            }, new Array<React.ReactElement>())}
          </div>
        )
      },
    },
    {
      field: 'id',
      headerName: 'N° demande',
      width: 110,
    },
    {
      field: 'createdAt',
      headerName: 'Date de création',
      valueGetter: ({ row }) =>
        moment(row.createdAt, 'YYYY-MM-DD').format('DD/MM/YYYY'),
      width: 140,
    },
    {
      field: 'status',
      headerName: 'Statut',
      renderCell: ({ row }) => (
        <Chip
          color={
            row.status === MssRequestStatus.APPROVED ? 'primary' : 'default'
          }
          label={MssRequestStatusLabels[row.status]}
        ></Chip>
      ),
      width: 160,
    },
    {
      field: 'patient',
      headerName: 'Patient',
      valueGetter: ({ row }) =>
        `${row.patient.firstName} ${row.patient.lastName} ${
          row.patient.birthDate
            ? `(${moment(row.patient.birthDate, 'YYYY-MM-DD').format(
                'DD/MM/YYYY',
              )})`
            : ''
        }`,
      width: 200,
    },
    {
      field: 'priority',
      headerName: 'Rdv priorité',
      valueGetter: ({ row }) => MssRequestPriorityLabels[row.priority],
      width: 120,
    },
    {
      field: 'priorityComment',
      headerName: 'Rdv commentaire',
      width: 150,
    },
    {
      field: 'site',
      headerName: 'Site',
      valueGetter: ({ row }) => row.site.label,
      width: 140,
    },
    {
      field: 'functionalUnit',
      headerName: 'Unité fonctionnelle',
      valueGetter: ({ row }) => row.functionalUnit.label,
      width: 160,
    },
    {
      field: 'referringDoctor',
      headerName: 'Médecin demandeur',
      valueGetter: ({ row }) =>
        `${row.referringDoctor.firstName} ${row.referringDoctor.lastName}`,
      width: 160,
    },
    {
      field: 'documents',
      headerName: 'Documents',
      width: 110,
      renderCell: ({ row }) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {row.documents.length} <AttachmentIcon style={{ marginLeft: 4 }} />
          </div>
        )
      },
    },
    {
      field: 'indication',
      headerName: 'Indication',
      width: 180,
    },
    {
      field: 'exams',
      headerName: 'Examens',
      width: 140,
      valueGetter: ({ row }) =>
        (row as MssRequestDto).exams.map(({ label }) => label).join(', '),
    },
    {
      field: 'updatedAt',
      headerName: 'Dernière action le',
      width: 150,
      valueGetter: ({ row }) => moment(row.updatedAt).format('DD/MM/YYYY'),
    },
    {
      field: 'lastActionBy',
      headerName: 'par',
      width: 120,
      valueGetter: ({ row }) =>
        row.updatedBy
          ? `${row.updatedBy.firstName ?? ''} ${row.updatedBy.lastName ?? ''}`
          : `${row.createdBy.firstName ?? ''} ${row.createdBy.lastName ?? ''}`,
    },
  ]

  return (
    <Container maxWidth={false}>
      <Grid container spacing={4} className="h-[calc(100vh-100px)]">
        <Grid item xs={12} className="h-full">
          <div className="h-full w-full bg-white rounded-t-[15px] rounded-b-[25px] overflow-hidden">
            <DataGridPro
              density="standard"
              className="h-full w-full"
              columns={columns}
              rows={requests.datas}
              rowCount={requests.totalCount}
              disableSelectionOnClick
              pagination
              paginationMode="server"
              pageSize={20}
              onPageChange={(page) => {
                dispatch(setMssRequestFilters({ page }))
              }}
              components={{
                Toolbar: () => <MssRequestsTableToolbar />,
              }}
              loading={loading}
            />
          </div>
        </Grid>
      </Grid>

      <Dialog
        fullScreen
        open={!!editingRequest}
        onClose={() => setEditingRequest(null)}
        TransitionComponent={UpTransition}
        slotProps={{ backdrop: {} }}
      >
        <DialogContent>
          {!!editingRequest && (
            <MssRequestForm
              onClose={() => setEditingRequest(null)}
              editingRequest={editingRequest}
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  )
}
